import React from 'react';

import { Select } from '@aline/common';
import { closestCenter, DndContext } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { IconButton, Typography } from '@mui/material';
import { CirclePlus, ListOrdered, ScrollText } from 'lucide-react';
import { AlineTooltip } from 'src/components';
import { InlineEditField } from 'src/components/generic/InlineEditInput';
import { useUpdatePlaybookRule } from 'src/hooks';

import { PlaybookAddButton } from './playbook-add-button';
import { PositionCard } from './PositionCard';
import { useRuleDetailsStyles } from './rule-details-styles';
import type { PlaybookDetailRule } from './types';
import { useRulePositions } from './use-rule-positions';

interface RuleDetailsPanelProps {
  rule: PlaybookDetailRule;
  disabled?: boolean;
}

/**
 * Editable details for the selected rule: its positions as one ordered,
 * reorderable list. Index 0 is the Starting Position and the rest are its
 * fallbacks, so dragging a fallback to the top promotes it. Each field commits
 * on blur straight to the position it edits — see `useRulePositions`, which owns
 * every one of those writes.
 */
export function RuleDetailsPanel({ disabled = false, rule }: RuleDetailsPanelProps): JSX.Element {
  const { classes } = useRuleDetailsStyles();
  const { updatePlaybookRule } = useUpdatePlaybookRule();
  const {
    addFallback,
    addedPositionId,
    commitPosition,
    deletePosition,
    handleDragEnd,
    isEmptyRule,
    positions,
    sensors,
  } = useRulePositions(rule);

  return (
    <div className={classes.panel} data-cy={Select.playbooks.ruleDetails}>
      <PanelHeader />

      <div className={classes.panelContent}>
        <div className={classes.fieldGroup} data-cy={Select.playbooks.ruleTitle}>
          <InlineEditField
            classes={{
              display: classes.titleDisplay,
              input: classes.titleInput,
              placeholder: classes.titlePlaceholder,
            }}
            disabled={disabled}
            editTooltip="Rename rule"
            iconSize={16}
            /**
             * A rule created from "Add rule" opens here with nothing in it, so
             * the caret starts at its name. The panel is keyed by rule id, so
             * this fires once per newly opened rule rather than on every render.
             */
            initiallyEditing={isEmptyRule}
            placeholder="Type a rule title..."
            textPlaceholder="Untitled rule"
            value={rule.title}
            onSave={title =>
              /**
               * An emptied field leaves the rule unnamed rather than putting the
               * old name back — the field already committed the trimmed text, so
               * the empty string is what "nobody has named this" is stored as.
               */
              updatePlaybookRule({ props: { title }, ruleId: rule.id })
            }
          />
        </div>

        <div>
          <div className={classes.positionsHeader}>
            <Typography className={classes.positionsTitle} variant="subtitle1">
              <ListOrdered size={16} />
              Positions
            </Typography>
            <AlineTooltip arrow placement="top" title="Add a new position">
              <IconButton
                className={classes.addHeaderButton}
                data-cy={Select.playbooks.addPositionHeader}
                disabled={disabled}
                size="small"
                onClick={() => void addFallback()}
              >
                <CirclePlus size={16} />
              </IconButton>
            </AlineTooltip>
          </div>

          <DndContext
            collisionDetection={closestCenter}
            sensors={sensors}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={positions.map(position => position.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className={classes.positionList}>
                {positions.map((position, index) => (
                  <PositionCard
                    key={position.id}
                    autoFocus={position.id === addedPositionId}
                    canDelete={positions.length > 1}
                    disabled={disabled}
                    index={index}
                    position={position}
                    onCommit={props => commitPosition(position.id, props)}
                    onDelete={() => deletePosition(position.id)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          <div className={classes.addPositionRow}>
            <PlaybookAddButton
              dataCy={Select.playbooks.addPosition}
              disabled={disabled}
              onClick={() => void addFallback()}
            >
              Add Position
            </PlaybookAddButton>
          </div>
        </div>
      </div>
    </div>
  );
}

function PanelHeader(): JSX.Element {
  const { classes } = useRuleDetailsStyles();

  return (
    <div className={classes.panelHeader}>
      <span className={classes.panelTitle}>
        <ScrollText size={16} />
        Rule Details
      </span>
    </div>
  );
}
